import React, { useState, useRef } from 'react'
import { Lock, Plus, Pencil, Trash2, Loader2, Upload, X, AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/ui/PageHeader'
import { useTenant } from '../hooks/useTenant'
import {
  useCostEntries,
  useCostEntryCategories,
  useCreateCostEntry,
  useUpdateCostEntry,
  useDeleteCostEntry,
  useImportCostEntries,
  CostEntry,
} from '../hooks/useCostEntries'

import { formatKpiEur } from '../lib/formatters'

// ---------------------------------------------------------------------------
// Paywall
// ---------------------------------------------------------------------------

function GrowthPaywall() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
      <div className="rounded-full bg-indigo-500/10 p-5">
        <Lock className="w-10 h-10 text-indigo-400" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-[--km-text-primary] mb-2">
          Funzionalità Growth+
        </h2>
        <p className="text-[--km-text-secondary] max-w-md">
          La gestione costi aziendali è disponibile dal piano Growth.
          Registra OPEX, costi diretti e indiretti per calcolare l'EBITDA reale.
        </p>
      </div>
      <Link
        to="/upgrade"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-medium transition-colors"
      >
        Scopri il piano Growth
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form crea / modifica
// ---------------------------------------------------------------------------

interface EntryFormData {
  category: string
  description: string
  amount: string
  competence_date: string
  cost_type: string
  behavior_type: string
}

const EMPTY_FORM: EntryFormData = {
  category: '',
  description: '',
  amount: '',
  competence_date: new Date().toISOString().slice(0, 10),
  cost_type: '',
  behavior_type: '',
}

interface EntryFormProps {
  initial?: CostEntry
  onSave: (data: EntryFormData) => void
  onCancel: () => void
  isLoading: boolean
}

function EntryForm({ initial, onSave, onCancel, isLoading }: EntryFormProps) {
  const [form, setForm] = useState<EntryFormData>(
    initial
      ? {
          category: initial.category,
          description: initial.description ?? '',
          amount: String(initial.amount),
          competence_date: initial.competence_date,
          cost_type: initial.cost_type ?? '',
          behavior_type: initial.behavior_type ?? '',
        }
      : EMPTY_FORM
  )

  const set = (k: keyof EntryFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div className="glass rounded-2xl p-6 border border-white/10 mb-6">
      <h3 className="text-base font-semibold text-[--km-text-primary] mb-4">
        {initial ? 'Modifica voce di costo' : 'Nuova voce di costo'}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Categoria *</label>
          <input
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.category}
            onChange={set('category')}
            placeholder="es. Affitto, Software, Consulenza"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Importo (€) *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.amount}
            onChange={set('amount')}
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Data competenza *</label>
          <input
            type="date"
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.competence_date}
            onChange={set('competence_date')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Tipo costo</label>
          <select
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.cost_type}
            onChange={set('cost_type')}
          >
            <option value="">— Seleziona —</option>
            <option value="direct">Diretto</option>
            <option value="indirect">Indiretto</option>
            <option value="overhead">Overhead</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Comportamento</label>
          <select
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.behavior_type}
            onChange={set('behavior_type')}
          >
            <option value="">— Seleziona —</option>
            <option value="fixed">Fisso</option>
            <option value="variable">Variabile</option>
            <option value="semi_variable">Semi-variabile</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[--km-text-muted] mb-1">Descrizione</label>
          <input
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
            value={form.description}
            onChange={set('description')}
            placeholder="Note opzionali"
          />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => onSave(form)}
          disabled={isLoading || !form.category || !form.amount || !form.competence_date}
          className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-[--km-text-secondary] text-sm font-medium transition-colors"
        >
          Annulla
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

const COST_TYPE_LABELS: Record<string, string> = {
  direct: 'Diretto', indirect: 'Indiretto', overhead: 'Overhead',
}
const COST_TYPE_COLORS: Record<string, string> = {
  direct: 'bg-blue-500/10 text-blue-400',
  indirect: 'bg-yellow-500/10 text-yellow-400',
  overhead: 'bg-purple-500/10 text-purple-400',
}
const BEHAVIOR_LABELS: Record<string, string> = {
  fixed: 'Fisso', variable: 'Variabile', semi_variable: 'Semi-var.',
}

// ---------------------------------------------------------------------------
// Import CSV modal
// ---------------------------------------------------------------------------

function ImportModal({ onClose }: { onClose: () => void }) {
  const importMutation = useImportCostEntries()
  const fileRef = useRef<HTMLInputElement>(null)
  const [result, setResult] = useState<{ rows_imported: number; rows_error: number; errors: Array<{ row: number; error: string }> } | null>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const res = await importMutation.mutateAsync(file)
      setResult(res)
    } catch (err) {
      // handled by mutation state
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="glass rounded-2xl p-6 border border-white/10 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[--km-text-primary]">Importa CSV costi</h3>
          <button onClick={onClose} className="text-[--km-text-muted] hover:text-[--km-text-primary]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-[--km-text-secondary] mb-4">
              Colonne obbligatorie: <code className="text-indigo-400">competence_date</code>, <code className="text-indigo-400">amount</code>, <code className="text-indigo-400">category</code>.<br />
              Opzionali: <code className="text-[--km-text-muted]">description, cost_type, behavior_type, external_id</code>.<br />
              Formati data accettati: <code className="text-[--km-text-muted]">DD/MM/YYYY</code>, <code className="text-[--km-text-muted]">YYYY-MM-DD</code>.
            </p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importMutation.isPending}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-indigo-500/50 text-[--km-text-secondary] hover:text-[--km-text-primary] transition-colors disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span className="text-sm">{importMutation.isPending ? 'Importazione...' : 'Seleziona file CSV'}</span>
            </button>
            {importMutation.isError && (
              <div className="mt-3 flex items-start gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{(importMutation.error as Error).message}</span>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4 mb-4">
              <p className="text-sm text-green-400 font-medium">
                Importate {result.rows_imported} voci ({result.rows_error} errori)
              </p>
            </div>
            {result.errors.length > 0 && (
              <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-400">Riga {e.row}: {e.error}</p>
                ))}
              </div>
            )}
            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors">
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function CostEntriesPage() {
  const { data: tenant, isLoading: tenantLoading } = useTenant()
  const plan = tenant?.plan ?? 'trial'
  const isGrowthPlus = ['growth', 'enterprise'].includes(plan)

  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [categoryFilter, setCategoryFilter] = useState('')

  const { data, isLoading, isError } = useCostEntries({ year, category: categoryFilter || undefined })
  const { data: categories } = useCostEntryCategories()
  const createEntry = useCreateCostEntry()
  const updateEntry = useUpdateCostEntry()
  const deleteEntry = useDeleteCostEntry()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<CostEntry | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[--km-text-muted]" />
      </div>
    )
  }

  if (!isGrowthPlus) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <PageHeader title="Costi & OPEX" subtitle="Gestisci i costi aziendali e calcola l'EBITDA reale" />
        <GrowthPaywall />
      </div>
    )
  }

  async function handleSave(form: EntryFormData) {
    setFormError(null)
    try {
      if (editing) {
        await updateEntry.mutateAsync({
          id: editing.id,
          body: {
            category: form.category,
            description: form.description || undefined,
            amount: form.amount,
            competence_date: form.competence_date,
            cost_type: form.cost_type || undefined,
            behavior_type: form.behavior_type || undefined,
          },
        })
      } else {
        await createEntry.mutateAsync({
          category: form.category,
          description: form.description || undefined,
          amount: form.amount,
          competence_date: form.competence_date,
          cost_type: form.cost_type || undefined,
          behavior_type: form.behavior_type || undefined,
        })
      }
      setShowForm(false)
      setEditing(null)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Errore salvataggio')
    }
  }

  async function handleDelete(entry: CostEntry) {
    if (!confirm(`Eliminare la voce "${entry.category}" (${formatKpiEur(entry.amount)})?`)) return
    await deleteEntry.mutateAsync(entry.id)
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalAmount = items.reduce((s, e) => s + e.amount, 0)

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        title="Costi & OPEX"
        subtitle={`${total} voci · ${formatKpiEur(totalAmount)} totale ${year}`}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
        >
          {[currentYear, currentYear - 1, currentYear - 2].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500"
        >
          <option value="">Tutte le categorie</option>
          {(categories ?? []).map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-[--km-text-secondary] text-sm font-medium transition-colors border border-white/10"
          >
            <Upload className="w-4 h-4" />
            Importa CSV
          </button>
          <button
            onClick={() => { setEditing(null); setShowForm(true) }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi
          </button>
        </div>
      </div>

      {/* Form crea/modifica */}
      {(showForm || editing) && (
        <EntryForm
          initial={editing ?? undefined}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null) }}
          isLoading={createEntry.isPending || updateEntry.isPending}
        />
      )}
      {formError && (
        <div className="mb-4 flex items-start gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      {/* Lista voci */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[--km-text-muted]" />
        </div>
      ) : isError ? (
        <div className="rounded-2xl glass border border-white/10 p-8 text-center text-[--km-text-muted]">
          Dati non disponibili. Riprova più tardi.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl glass border border-white/10 p-8 text-center text-[--km-text-muted]">
          Nessuna voce di costo per {year}. Aggiungi la prima voce o importa un CSV.
        </div>
      ) : (
        <div className="glass rounded-2xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-medium text-[--km-text-muted]">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[--km-text-muted]">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[--km-text-muted]">Tipo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[--km-text-muted]">Importo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[--km-text-muted]">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map(entry => (
                <tr key={entry.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 text-[--km-text-secondary] whitespace-nowrap">
                    {entry.competence_date}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-[--km-text-primary]">{entry.category}</span>
                    {entry.description && (
                      <span className="ml-2 text-xs text-[--km-text-muted]">{entry.description}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {entry.cost_type && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${COST_TYPE_COLORS[entry.cost_type] ?? 'bg-white/10 text-[--km-text-muted]'}`}>
                          {COST_TYPE_LABELS[entry.cost_type] ?? entry.cost_type}
                        </span>
                      )}
                      {entry.behavior_type && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[--km-text-muted]">
                          {BEHAVIOR_LABELS[entry.behavior_type] ?? entry.behavior_type}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-[--km-text-primary] whitespace-nowrap">
                    {formatKpiEur(entry.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditing(entry); setShowForm(false) }}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(entry)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-[--km-text-muted] hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </div>
  )
}
